import { NgForOf, NgIf, CommonModule, DatePipe } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import {
  FormsModule,
  ReactiveFormsModule,
  FormGroup,
  FormControl,
} from "@angular/forms";
import { RouterLink } from "@angular/router";
import { TuiTable } from "@taiga-ui/addon-table";
import {
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiLink,
  TuiTextfield,
  TuiLoader,
  tuiLoaderOptionsProvider,
  TuiAlertService,
  TuiHint,
} from "@taiga-ui/core";
import {
  TuiDataListWrapper,
  TuiBreadcrumbs,
  TuiPagination,
  TuiFade,
  TuiTabs,
  TuiStatus,
  TuiItemsWithMore,
} from "@taiga-ui/kit";
import { TuiNavigation, TuiSearch } from "@taiga-ui/layout";
import {
  TuiInputSliderModule,
  TuiTextfieldControllerModule,
  TuiComboBoxModule,
  TuiInputDateModule,
  TuiInputDateRangeModule,
} from "@taiga-ui/legacy";

import { ManualRecordService } from "../../core/services/manualRecords.service";
import { TuiDay, TuiDayRange } from "@taiga-ui/cdk";
import { Subscription } from "rxjs";
import { DisableUnlessPermissionDirective } from "../../core/directives/disable-unless-permission.directive";

@Component({
  selector: "app-manual-records",
  standalone: true,
  imports: [
    DisableUnlessPermissionDirective,
    FormsModule,
    TuiInputDateRangeModule,
    NgForOf,
    NgIf,
    TuiAppearance,
    TuiDataListWrapper,
    TuiBreadcrumbs,
    TuiButton,
    TuiDataList,
    TuiPagination,
    TuiDropdown,
    TuiFade,
    TuiTable,
    TuiLink,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiStatus,
    TuiItemsWithMore,
    TuiSearch,
    TuiHint,
    ReactiveFormsModule,
    CommonModule,
    TuiInputSliderModule,
    TuiTextfieldControllerModule,
    RouterLink,
    TuiComboBoxModule,
    TuiLoader,
    TuiInputDateModule,
  ],
  templateUrl: "./manual-records.component.html",
  styleUrl: "./manual-records.component.scss",
  providers: [DatePipe, tuiLoaderOptionsProvider({ size: "l" })],
})
export class ManualRecordsComponent implements OnInit {
  protected form = new FormGroup({
    search: new FormControl(""),
    dateRange: new FormControl<[Date | null, Date | null] | null>(null),
  });

  protected readonly breadcrumbs = ["Royal Hospital", "Manual Records"];
  protected readonly sizes = ["m"] as const;
  protected size = this.sizes[0];

  procedures: any[] = [];
  nurses: any[] = [];
  clients: any[] = [];

  totalCharge = 0;
  totalPayment = 0;
  totalGrandTotal = 0;
  isLoading = false;
  total = 0;
  pageSize = 10;
  pageIndex = 1;
  startDate: string = "";
  endDate: string = "";
  dateRange: Date[] = [];

  private subscription: Subscription;

  constructor(
    private manualRecordService: ManualRecordService,
    private alertService: TuiAlertService,
    private datePipe: DatePipe
  ) {
    this.subscription = this.dateForm
      .get("testValue")!
      .valueChanges.subscribe((value) => {
        if (value) {
          this.startDate = this.formatTuiDay(value.from);
          this.endDate = this.formatTuiDay(value.to);

          console.log("Selected Date Range:", value);
          console.log("Formatted Start Date:", this.startDate);
          console.log("Formatted End Date:", this.endDate);
        }
      });
  }

  ngOnInit(): void {
    this.loadManualRecords();
  }

  private getFirstAndLastDayOfCurrentMonth(): TuiDayRange {
    const now = new Date();

    const firstDay = new TuiDay(now.getFullYear(), now.getMonth(), 1);

    const lastDate = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0
    ).getDate();
    const lastDay = new TuiDay(now.getFullYear(), now.getMonth(), lastDate);
    this.startDate = this.formatTuiDay(firstDay);
    this.endDate = this.formatTuiDay(lastDay);
    return new TuiDayRange(firstDay, lastDay);
  }

  protected readonly dateForm = new FormGroup({
    testValue: new FormControl(this.getFirstAndLastDayOfCurrentMonth()),
  });
  private formatTuiDay(tuiDay: TuiDay): string {
    return `${tuiDay.year}-${String(tuiDay.month + 1).padStart(
      2,
      "0"
    )}-${String(tuiDay.day).padStart(2, "0")}`;
  }

  loadManualRecords(): void {
    const startDate = this.startDate;
    const endDate = this.endDate;

    this.dateRange = this.generateDateRange(
      new Date(startDate),
      new Date(endDate)
    );

    const formValue = this.form.value;

    const payload: any = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      search: formValue.search || "",
      startDate: this.startDate,
      endDate: this.endDate,
    };

    this.isLoading = true;

    this.manualRecordService.getManualRecordsByDateRange(payload).subscribe({
      next: (res: any) => {
        if (res.records) {
          this.procedures = res.records;
          this.total = res.totalPages * this.pageSize;
          this.totalCharge = res.totalCharge;
          this.totalPayment = res.totalPayment;
          this.totalGrandTotal = res.grandTotal;
          console.log("Procedures loaded:", this.procedures);
        } else {
          this.procedures = [];
          this.total = 0;
          this.totalCharge = 0;
          this.totalPayment = 0;
          this.totalGrandTotal = 0;
        }
      },
      error: (err) => {
        console.error("Error fetching procedures:", err);
        this.alertService
          .open("Failed to load procedures.", {
            appearance: "error",
            label: "Error",
          })
          .subscribe();
      },
      complete: () => (this.isLoading = false),
    });
  }

  onPageChange(page: number): void {
    this.pageIndex = page;
    this.loadManualRecords();
  }

  onSearchEnter(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      this.loadManualRecords();
    }
  }
  generateDateRange(startDate: Date, endDate: Date): Date[] {
    const range = [];
    let currentDate = startDate;
    while (currentDate <= endDate) {
      range.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return range;
  }

  clearFilters(): void {
    this.form.reset();
    this.loadManualRecords();
  }

  getPageCount(): number {
    return Math.ceil(this.total / this.pageSize);
  }

  getPaymentBreakdown(record: any): string {
    if (!record.payments && !record.customPayments) {
      return 'No payment details available';
    }

    let breakdown = 'Payment Breakdown:\n\n';
    
    // Standard payments
    if (record.payments) {
      const standardPayments = [
        { name: 'Doctor Payment', value: record.payments.doctorPayment },
        { name: 'Nursing Payment', value: record.payments.nursingPayment },
        { name: 'Pharmacy Payment', value: record.payments.pharmacyPayment },
        { name: 'Laboratory Payment', value: record.payments.laboratoryPayment },
        { name: 'Ambulance Payment', value: record.payments.ambulancePayment },
        { name: 'Physiotherapy Payment', value: record.payments.physiotherapyPayment },
        { name: 'Other Payment', value: record.payments.otherPayment }
      ];

      standardPayments.forEach(payment => {
        if (payment.value && payment.value > 0) {
          breakdown += `${payment.name}: LKR ${payment.value}\n`;
        }
      });
    }

    // Custom payments
    if (record.customPayments && record.customPayments.length > 0) {
      breakdown += '\nCustom Payments:\n';
      record.customPayments.forEach((customPayment: any) => {
        if (customPayment.value && customPayment.value > 0) {
          breakdown += `${customPayment.name}: LKR ${customPayment.value}\n`;
        }
      });
    }

    breakdown += `\nGrand Total: LKR ${record.grandTotal || 0}`;
    
    return breakdown;
  }
}
